import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send, Mail } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";

export const portalLink = (tab) => `${window.location.origin}/?tab=${tab}`;

const toB64 = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(blob); });

export const EmailDocButton = ({ employeeId, employeeName, pdf, refId, title = "", testid, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { blob, filename } = await pdf();
      const fd = new FormData();
      fd.append("file", blob, filename || "document.pdf");
      fd.append("title", t.trim() || "مستند");
      fd.append("ref_id", refId || "");
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeId}/email-doc`, fd).then((x) => x.data);
      toast.success(`أُرسل المستند إلى ${r.to}`); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  const clean = (s) => s.replace(/[*_]/g, "").replace(/^[^\p{L}\p{N}]+/u, "").trim().slice(0, 80);
  return (
    <>
      <button type="button" title="إرسال بالإيميل" data-testid={testid || `email-send-${refId || employeeId}`}
        onClick={(e) => { e.stopPropagation(); setT(clean(title)); setOpen(true); }}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-blue-600 hover:bg-blue-50 ${className}`}>
        <Mail className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="w-[95vw] max-w-md" data-testid="email-send-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><Mail className="h-5 w-5 text-blue-600" /> إرسال بالإيميل إلى {employeeName || "الموظف"}</DialogTitle>
            <DialogDescription className="text-start">يوصل الموظف على بريده المسجّل رابط آمن يفتح ملف PDF.</DialogDescription></DialogHeader>
          <label className="text-xs text-slate-500">عنوان المستند</label>
          <input value={t} onChange={(e) => setT(e.target.value)} className="w-full rounded-lg border border-slate-200 p-2.5 text-sm" placeholder="مثال: مسير راتب شهر 10" data-testid="email-send-title" />
          <Button onClick={send} disabled={busy || !t.trim()} className="w-full gap-2 bg-blue-600 hover:bg-blue-700" data-testid="email-send-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy ? "جارٍ تجهيز الملف والإرسال…" : "إرسال بالإيميل"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const WaSendButton = ({ employeeId, employeeName, text = "", refType = "manual", refId, testid, className = "", pdf, emailTitle }) => {
  const [open, setOpen] = useState(false);
  const [attach, setAttach] = useState(true);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  if (!employeeId) return null;
  const send = async () => {
    setBusy(true);
    try {
      let extra = {};
      if (pdf && attach) { const { blob, filename } = await pdf(); extra = { pdf_base64: await toB64(blob), filename }; }
      const r = await api.waSend({ employee_ids: [employeeId], body, ref_type: refType, ref_id: refId, ...extra });
      if (r.sent) { toast.success(`أُرسلت الرسالة إلى ${employeeName || "الموظف"}`); setOpen(false); }
      else toast.error(r.no_phone_count ? "لا يوجد رقم جوال مسجّل لهذا الموظف" : `لم تُرسل: ${r.failed?.[0]?.reason || "خطأ"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  return (
    <>
      {pdf && <EmailDocButton employeeId={employeeId} employeeName={employeeName} pdf={pdf} refId={refId} title={emailTitle || (text || "").split("\n")[0]} testid={`email-send-${refId || employeeId}`} />}
      <button type="button" title="إرسال واتساب" data-testid={testid || `wa-send-${refId || employeeId}`}
        onClick={(e) => { e.stopPropagation(); setBody(text); setOpen(true); }}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 ${className}`}>
        <MessageCircle className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="w-[95vw] max-w-md" data-testid="wa-send-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><MessageCircle className="h-5 w-5 text-emerald-600" /> رسالة واتساب إلى {employeeName || "الموظف"}</DialogTitle>
            <DialogDescription className="text-start">تُرسل من رقم واتساب الشركة إلى جوال الموظف المسجّل. عدّل النص قبل الإرسال.</DialogDescription></DialogHeader>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="w-full rounded-lg border border-slate-200 p-3 text-sm" data-testid="wa-send-text" />
          {pdf && <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} data-testid="wa-send-attach" /> إرفاق ملف PDF مع الرسالة</label>}
          <Button onClick={send} disabled={busy || !body.trim()} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="wa-send-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy && pdf && attach ? "جارٍ تجهيز الملف والإرسال…" : "إرسال"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
